import { useMemo } from 'react';
import type { Booking } from '@/types';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { isUnallocatedBooking } from '@/utils/unallocated-booking';

export type UrgencyLevel = 'critical' | 'soon' | 'upcoming';

export interface UnallocatedBooking extends Booking {
  urgency: UrgencyLevel;
  daysUntilStart: number;
}

/**
 * Returns all bookings assigned to the 'unallocated' resource,
 * enriched with urgency metadata and sorted soonest-first.
 *
 * In live mode, unallocated bookings have a null resourceId rather
 * than the literal string 'unallocated' used by demo data.
 */
export function useUnallocatedBookings(bookings: Booking[], isLive = false): UnallocatedBooking[] {
  return useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return bookings
      .filter((booking) => isUnallocatedBooking(booking, isLive))
      .map(b => {
        const start = parseISO(b.startTime);
        const daysUntilStart = differenceInCalendarDays(start, today);

        let urgency: UrgencyLevel;
        if (daysUntilStart <= 3) {
          urgency = 'critical';
        } else if (daysUntilStart <= 7) {
          urgency = 'soon';
        } else {
          urgency = 'upcoming';
        }

        return { ...b, urgency, daysUntilStart };
      })
      .sort((a, b) => a.daysUntilStart - b.daysUntilStart);
  }, [bookings, isLive]);
}
